import { type IOnboardingChapter, ETourId } from './types';
import { getTourConfig } from './registry';
import { isTourCompleted } from './persistence';

/**
 * The unified onboarding flow, organized into chapters.
 *
 * Each chapter groups related tours and defines a title shown on tour cards.
 * Step counts displayed to the user will be scoped per chapter.
 *
 * Chapters:
 * 1. Essentials: Drafts landing page, draft editor chat, draft automation edit, triggers
 * 2. Run & Publish: Successful draft run, viewing published automation
 * 3. Monitor: Breadcrumb intro on automation details, automation list dashboard, guidance center, guidance center action item
 * 4. Exceptions: Exception highlight
 */
export const ONBOARDING_CHAPTERS: readonly IOnboardingChapter[] = [
  {
    titleKey: 'tours.singleFlow.chapter.essentials',
    tours: [
      ETourId.DRAFT_WELCOME,
      ETourId.DRAFT_EDITOR,
      ETourId.DRAFT_AUTOMATION_EDIT_VIEW,
      ETourId.INPUTS_TRIGGERS,
    ],
  },
  {
    titleKey: 'tours.singleFlow.chapter.runAndPublish',
    tours: [ETourId.SUCCESSFUL_DRAFT_RUN, ETourId.VIEWING_PUBLISHED_AUTOMATION],
  },
  {
    titleKey: 'tours.singleFlow.chapter.monitor',
    tours: [
      ETourId.MONITOR_INTRO,
      ETourId.AUTOMATION_LIST_DASHBOARD,
      ETourId.GUIDANCE_CENTER,
      ETourId.GUIDANCE_CENTER_ACTION_ITEM,
    ],
  },
  {
    titleKey: 'tours.singleFlow.chapter.exceptions',
    tours: [ETourId.EXCEPTION_HIGHLIGHT],
    conditional: true,
  },
] as const;

/**
 * Step offset (count of steps in earlier tours of the same chapter) and
 * total chapter step count for a given tour.
 *
 * Returns null when the tour is not part of any onboarding chapter.
 */
export function getChapterStepInfo(
  tourId: ETourId,
): { offset: number; total: number } | null {
  const chapter = ONBOARDING_CHAPTERS.find(c => c.tours.includes(tourId));
  if (!chapter) return null;

  // If the tour has a chapterStepCount, use that instead of the steps.length
  const stepsOf = (id: ETourId) => {
    const config = getTourConfig(id);
    return config.chapterStepCount ?? config.steps.length;
  };
  const index = chapter.tours.indexOf(tourId);
  const offset = chapter.tours
    .slice(0, index)
    .reduce((sum, id) => sum + stepsOf(id), 0);
  const total = chapter.tours.reduce((sum, id) => sum + stepsOf(id), 0);

  return { offset, total };
}

/**
 * Returns true if every tour preceding `tourId` in the flattened onboarding
 * flow has been completed (or skipped — `markTourSkipped` writes the same
 * flag). Non-onboarding tours and the flow's first tour always return true.
 *
 * Conditional chapters (e.g. Exceptions) are excluded from the flow: their
 * tours are contextual interrupts that fire when their trigger fires, not
 * linearly after the preceding chapter.
 *
 * DRAFT_WELCOME is excluded from the flow: it uses a linked-pair persistence
 * pattern (persists only when DRAFT_EDITOR finishes) and can't be relied on
 * as a localStorage signal.
 *
 * Used by `TourTrigger` to gate automatic starts so tours only appear in
 * order.
 */
export function arePriorOnboardingToursCompleted(tourId: ETourId): boolean {
  const flow: ETourId[] = ONBOARDING_CHAPTERS.filter(c => !c.conditional)
    .flatMap(c => c.tours)
    .filter(id => id !== ETourId.DRAFT_WELCOME);
  const index = flow.indexOf(tourId);
  if (index <= 0) return true;
  return flow
    .slice(0, index)
    .every(id => isTourCompleted(id, getTourConfig(id).persistence));
}
